import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/site/Form";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { SettingsPanel } from "./IntegrationStatus";

const RET = [
  ["closed_months", "Delete purchased / not proceeding enquiries after (months)"], ["inactive_months", "Delete any enquiry with no activity after (months)"],
  ["test_days", "Delete test enquiries after (days)"], ["draft_days", "Delete photos from unsent enquiries after (days)"],
];

const DEMO_CARS = [
  ["SA22MZD", "Fiat 500 (2022)"],
  ["SL13AFY", "Kia Picanto (2013)"],
  ["RO22ACX", "Toyota Yaris (2022)"],
];

export const DemoGuide = () => (
  <SettingsPanel title="How to demonstrate the website" testid="demo-guide">
    <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-700">
      <li>Either stay signed in and click <strong>View website</strong> (same browser), or turn on <strong>Demo mode</strong> under Settings → Valuations so it works on any phone. Real registrations aren't in the sandbox – use the ones below.</li>
      <li>Enter one of the sandbox registrations below with any mileage. A yellow “Sandbox test mode” banner confirms it's test data, not a real valuation.</li>
      <li>Send the enquiry. It appears in <strong>Enquiries → New</strong>, marked with a small "Test" tag.</li>
    </ol>
    <ul className="space-y-1 text-sm" data-testid="demo-registrations">
      {DEMO_CARS.map(([r, d]) => <li key={r}><span className="plate-sm mr-2">{r}</span>{d}</li>)}
    </ul>
  </SettingsPanel>
);

export const DataSettings = ({ s, save, owner, reload }) => {
  const [r, setR] = useState(s.retention);
  const act = async (fn, ok) => {
    try { const { data } = await fn(); toast.success(ok(data)); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5">
      <DemoGuide />
      <SettingsPanel title="Retention" testid="retention-panel">
        <p className="text-sm text-slate-600">These periods are shown in the privacy information automatically. Clean-up runs daily{s.retention_last_run ? ` (last run ${fmtDateTime(s.retention_last_run)})` : ""}.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {RET.map(([k, label]) => <Field key={k} label={label} htmlFor={`r-${k}`}><input id={`r-${k}`} type="number" min="1" className="field-input" value={r[k]} disabled={!owner} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} data-testid={`retention-${k}-input`} /></Field>)}
        </div>
        {owner && <div className="flex flex-wrap gap-2">
          <Button onClick={() => save({ retention: r })} data-testid="retention-save-button">Save retention</Button>
          <Button variant="outline" onClick={() => act(() => api.post("/dealer/retention/run"), (d) => `Deleted ${d.enquiries_deleted} enquiries and ${d.orphan_photos_deleted} unsent photos`)} data-testid="retention-run-button">Run clean-up now</Button>
        </div>}
      </SettingsPanel>
      {owner && (
        <SettingsPanel title="Demo & test data" testid="demo-panel">
          <p className="text-sm text-slate-600">Demo enquiries are labelled DEMO, have no valuation and appear only under Test enquiries.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => act(() => api.post("/dealer/demo"), () => "Demo enquiries added")} data-testid="demo-seed-button">Add demo enquiries</Button>
            <Button variant="outline" onClick={() => act(() => api.delete("/dealer/demo"), (d) => `Removed ${d.deleted} demo enquiries`)} data-testid="demo-remove-button">Remove demo data</Button>
            <Button variant="destructive" onClick={() => window.confirm("Delete ALL test enquiries and their photos?") && act(() => api.delete("/dealer/test-enquiries"), (d) => `Deleted ${d.deleted} test enquiries`)} data-testid="delete-test-data-button">Delete all test enquiries</Button>
          </div>
        </SettingsPanel>
      )}
    </div>
  );
};
